import React, { useState, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Separator } from './ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Users,
  Clock,
  AlertTriangle,
  Lock,
  Sparkles,
  X,
  HelpCircle,
  User,
  UserPlus,
  Scissors,
  Save,
  Trash2,
  Info,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import './RuleDetailSheet.css';

// Group size configuration
const GROUP_SIZE_CONFIG = {
  1: { label: '1/4', description: 'Single player - needs 3 more', color: 'gray', fillPercent: 25 },
  2: { label: '2/4', description: 'Duo - needs 2 more', color: 'blue', fillPercent: 50 },
  3: { label: '3/4', description: 'Trio - needs 1 more', color: 'yellow', fillPercent: 75 },
  4: { label: '4/4', description: 'Full group - fills entire tee time', color: 'green', fillPercent: 100 },
};

// Rule type explanations
const RULE_EXPLANATIONS = {
  partner_group: {
    title: 'Partner Group Rule',
    description: 'These players requested to play together.',
    howItWorks: (playerCount) => {
      if (playerCount === 4) {
        return 'This group fills an entire tee time (4 slots). They will be assigned together to a single tee time with no other players.';
      } else if (playerCount === 3) {
        return 'This trio takes 3 of 4 slots. One additional player will be added to complete their group.';
      } else if (playerCount === 2) {
        return 'This duo takes 2 of 4 slots. Two additional players will be added to complete their group.';
      }
      return 'These players will be placed in the same group during tee sheet generation.';
    },
    tips: [
      'Higher priority groups are placed first',
      'If you need to separate players, use "Split Group" or delete this rule',
      'Required rules must be satisfied or generation will fail',
    ]
  },
  avoid_partner: {
    title: 'Avoid Partner Rule',
    description: 'These players should NOT play together.',
    howItWorks: () => 'The tee sheet generator will ensure these players are in different groups.',
    tips: [
      'Conflicts with partner_group rules for the same players',
      'Higher priority avoid rules take precedence',
    ]
  },
  time_constraint: {
    title: 'Time Constraint (Required)',
    description: 'This player MUST be assigned to this time slot.',
    howItWorks: () => 'Hard constraints must be satisfied. If not possible, tee sheet generation will fail.',
    tips: [
      'Use sparingly - too many hard constraints can make generation impossible',
      'Consider using time_preference for softer requirements',
    ]
  },
  time_preference: {
    title: 'Time Preference',
    description: 'This player prefers this time slot but it\'s flexible.',
    howItWorks: () => 'The generator will try to accommodate this preference, but may place the player elsewhere if needed.',
    tips: [
      'Higher priority preferences are satisfied first',
      'Conflicts are resolved automatically using player rankings',
    ]
  },
  handicap_sort: {
    title: 'Handicap Sort',
    description: 'Players are sorted by handicap index.',
    howItWorks: () => 'Lower handicap players get earlier tee times (can be configured).',
    tips: ['This is a system default rule', 'Can be overridden by time constraints/preferences']
  },
  pace_balance: {
    title: 'Pace Balance',
    description: 'Groups are balanced for pace of play.',
    howItWorks: () => 'Limits slow players per group to maintain good pace across the tournament.',
    tips: ['Default limit is 2 slow players per group', 'This helps prevent bottlenecks on the course']
  },
  manual_override: {
    title: 'Manual Override',
    description: 'Director has manually placed this player.',
    howItWorks: () => 'This placement is locked and will not be changed during generation.',
    tips: ['Use sparingly to maintain algorithm effectiveness', 'Drag players on the tee sheet to create overrides']
  },
};

function RuleDetailSheet({
  rule,
  open,
  onOpenChange,
  onSave,
  onDelete,
  onSplitGroup,
  players = []
}) {
  const [editedRule, setEditedRule] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (rule) {
      setEditedRule({ ...rule });
      setIsDirty(false);
    }
  }, [rule]);

  if (!rule || !editedRule) return null;

  const playerCount = rule.players?.length || 0;
  const groupSizeConfig = GROUP_SIZE_CONFIG[playerCount] || GROUP_SIZE_CONFIG[1];
  const ruleExplanation = RULE_EXPLANATIONS[rule.type] || RULE_EXPLANATIONS.partner_group;

  const handlePriorityChange = (value) => {
    setEditedRule(prev => ({ ...prev, priority: value[0] }));
    setIsDirty(true);
  };

  const handleFlexibilityChange = (value) => {
    setEditedRule(prev => ({ ...prev, flexibility: value }));
    setIsDirty(true);
  };

  const handleSave = () => {
    if (onSave) {
      onSave(editedRule);
    }
    setIsDirty(false);
  };

  const handleDelete = () => {
    if (onDelete) {
      onDelete(rule._id);
    }
    onOpenChange(false);
  };

  const handleSplitGroup = () => {
    if (onSplitGroup && playerCount > 2) {
      onSplitGroup(rule);
    }
  };

  const getFlexibilityBadgeVariant = (flex) => {
    switch (flex) {
      case 'hard': return 'destructive';
      case 'soft': return 'secondary';
      case 'flexible': return 'outline';
      default: return 'secondary';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'applied': return <CheckCircle2 className="h-4 w-4 text-green-600" />;
      case 'failed': return <XCircle className="h-4 w-4 text-red-600" />;
      case 'conflict': return <AlertTriangle className="h-4 w-4 text-orange-600" />;
      default: return <Clock className="h-4 w-4 text-gray-400" />;
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="rule-detail-sheet w-[450px] sm:max-w-[450px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            {ruleExplanation.title}
          </SheetTitle>
          <SheetDescription>
            {ruleExplanation.description}
          </SheetDescription>
        </SheetHeader>

        <div className="rule-detail-content">
          {/* Group Size Visual */}
          {rule.type === 'partner_group' && (
            <div className="group-size-section">
              <div className="section-label">
                <span>Group Size</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger>
                      <HelpCircle className="h-4 w-4 text-muted-foreground" />
                    </TooltipTrigger>
                    <TooltipContent side="right" className="max-w-[300px]">
                      <p>Each tee time has 4 slots. This shows how many slots this group fills.</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              <div className="group-size-visual">
                <div className="slot-grid">
                  {[1, 2, 3, 4].map((slot) => (
                    <div
                      key={slot}
                      className={cn(
                        "slot-indicator",
                        slot <= playerCount ? "filled" : "empty",
                        slot <= playerCount && `filled-${groupSizeConfig.color}`
                      )}
                    >
                      {slot <= playerCount ? (
                        <User className="h-4 w-4" />
                      ) : (
                        <UserPlus className="h-4 w-4" />
                      )}
                    </div>
                  ))}
                </div>
                <div className="group-size-label">
                  <span className={cn("size-badge", `bg-${groupSizeConfig.color}`)}>
                    {groupSizeConfig.label}
                  </span>
                  <span className="size-description">{groupSizeConfig.description}</span>
                </div>
              </div>

              {playerCount === 4 && (
                <div className="full-group-notice">
                  <Info className="h-4 w-4" />
                  <span>This group fills an entire tee time — no other players will be added.</span>
                </div>
              )}
            </div>
          )}

          <Separator />

          {/* Players List */}
          <div className="players-section">
            <div className="section-label">Players in Rule</div>
            <div className="players-list">
              {rule.playerNames?.map((name, idx) => (
                <div key={idx} className="player-chip">
                  <User className="h-3 w-3" />
                  <span>{name}</span>
                </div>
              ))}
            </div>
          </div>

          <Separator />

          {/* How It Works */}
          <div className="explanation-section">
            <div className="section-label">
              <span>How This Rule Works</span>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[300px]">
                    <p>Understanding how rules are applied during tee sheet generation.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <div className="explanation-box">
              <p>{ruleExplanation.howItWorks(playerCount)}</p>
            </div>
          </div>

          <Separator />

          {/* Priority Adjustment */}
          <div className="priority-section">
            <div className="section-label">
              <span>Priority Level</span>
              <Badge variant="outline" className="priority-value">
                {editedRule.priority}
              </Badge>
            </div>
            <div className="priority-slider">
              <span className="priority-label low">Higher</span>
              <Slider
                value={[editedRule.priority]}
                onValueChange={handlePriorityChange}
                min={0}
                max={200}
                step={1}
                className="flex-1"
              />
              <span className="priority-label high">Lower</span>
            </div>
            <p className="priority-hint">
              Lower numbers = higher priority (processed first). Director overrides: 0-99, Player requests: 100-150, System defaults: 150-200.
            </p>
          </div>

          <Separator />

          {/* Flexibility Setting */}
          <div className="flexibility-section">
            <div className="section-label">Flexibility</div>
            <Select
              value={editedRule.flexibility}
              onValueChange={handleFlexibilityChange}
            >
              <SelectTrigger className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="hard">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-red-600" />
                    <span>Required — Must be satisfied</span>
                  </div>
                </SelectItem>
                <SelectItem value="soft">
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4 text-blue-600" />
                    <span>Preferred — Try to satisfy</span>
                  </div>
                </SelectItem>
                <SelectItem value="flexible">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-600" />
                    <span>Flexible — Nice to have</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="flexibility-hint">
              {editedRule.flexibility === 'hard'
                ? 'Warning: Required rules that cannot be satisfied will cause generation to fail.'
                : editedRule.flexibility === 'soft'
                ? 'The generator will try to satisfy this but may override if needed.'
                : 'This rule can be easily overridden by higher priority rules.'}
            </p>
          </div>

          <Separator />

          {/* Status & Original Request */}
          <div className="status-section">
            <div className="status-row">
              <span className="status-label">Status:</span>
              <div className="status-value">
                {getStatusIcon(rule.status)}
                <span className="capitalize">{rule.status}</span>
              </div>
            </div>
            <div className="status-row">
              <span className="status-label">Source:</span>
              <Badge variant="outline" className="capitalize">
                {rule.source?.replace('_', ' ')}
              </Badge>
            </div>

            {/* Show individual player requests if available */}
            {rule.playerRequests && rule.playerRequests.length > 0 ? (
              <div className="player-requests-section">
                <div className="section-label" style={{ marginTop: '0.75rem' }}>
                  Original Requests
                </div>
                <div className="player-requests-list">
                  {rule.playerRequests.map((pr, idx) => (
                    <div key={idx} className="player-request-item">
                      <div className="player-request-name">
                        <User className="h-3 w-3" />
                        <span>{pr.playerName}</span>
                      </div>
                      <blockquote className="player-request-text">
                        "{pr.originalRequest}"
                      </blockquote>
                    </div>
                  ))}
                </div>
              </div>
            ) : rule.originalRequest ? (
              <div className="original-request">
                <span className="status-label">Original Request:</span>
                <blockquote>"{rule.originalRequest}"</blockquote>
              </div>
            ) : null}
          </div>

          {/* Tips */}
          <div className="tips-section">
            <div className="section-label">Tips</div>
            <ul className="tips-list">
              {ruleExplanation.tips?.map((tip, idx) => (
                <li key={idx}>
                  <Info className="h-3 w-3" />
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <SheetFooter className="rule-detail-footer">
          <div className="footer-actions-left">
            {rule.type === 'partner_group' && playerCount > 2 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSplitGroup}
                      className="split-btn"
                    >
                      <Scissors className="h-4 w-4 mr-1" />
                      Split Group
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Split this group into smaller pairs</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleDelete}
              className="delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          </div>
          <div className="footer-actions-right">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!isDirty}
            >
              <Save className="h-4 w-4 mr-1" />
              Save Changes
            </Button>
          </div>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

export default RuleDetailSheet;
